import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Calculator, ChevronDown, ChevronUp, Trash2, Send, Sparkles,
  Mail, Clock, CheckCircle, Eye, Loader2, Search, FileText,
  Plus, Save, Paperclip, Upload, X, File, Image, ZoomIn, Settings
} from 'lucide-react';
import { GraftCalculator } from '../../components/admin/GraftCalculator';
import { VoiceInput } from '../../components/ui/VoiceInput';

const API = process.env.REACT_APP_BACKEND_URL;
const t = (lang, de, en) => (lang === 'de' ? de : en);

function getAdminHeaders() {
  const token = localStorage.getItem('adminToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const treatmentLabels = { haar: 'Haartransplantation', bart: 'Barttransplantation', augenbrauen: 'Augenbrauentransplantation' };
const timelineLabels = { asap: 'Sofort', '1-3': '1-3 Monate', '3-6': '3-6 Monate', '6-12': '6-12 Monate', unsure: 'Unsicher' };
const statusConfig = {
  new: { de: 'Neu', color: 'bg-blue-500/20 text-blue-400', next: 'replied' },
  replied: { de: 'Beantwortet', color: 'bg-yellow-500/20 text-yellow-400', next: 'appointment' },
  appointment: { de: 'Termin', color: 'bg-purple-500/20 text-purple-400', next: 'completed' },
  completed: { de: 'Abgeschlossen', color: 'bg-emerald-500/20 text-emerald-400', next: null },
};

const PLACEHOLDERS = [
  { key: '{name}', desc: 'Name des Patienten' },
  { key: '{behandlung}', desc: 'Behandlungsart' },
  { key: '{muster}', desc: 'Muster/Bereich' },
  { key: '{alter}', desc: 'Alter' },
  { key: '{zeitrahmen}', desc: 'Gewünschter Zeitrahmen' },
];

function replacePlaceholders(text, inq) {
  if (!text || !inq) return text;
  return text
    .replace(/\{name\}/g, inq.name || '')
    .replace(/\{behandlung\}/g, treatmentLabels[inq.treatment] || inq.treatment || '')
    .replace(/\{muster\}/g, inq.pattern || '-')
    .replace(/\{alter\}/g, inq.age || '-')
    .replace(/\{zeitrahmen\}/g, timelineLabels[inq.timeline] || inq.timeline || '-');
}

const PriceInquiryAdmin = ({ skipAuth = false }) => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const [authenticated, setAuthenticated] = useState(skipAuth || !!localStorage.getItem('adminToken'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('inquiries');

  // Inquiry state
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(() => new URLSearchParams(window.location.search).get('inquiry'));
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTreatment, setFilterTreatment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Reply state
  const [replyingTo, setReplyingTo] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineInstructions, setRefineInstructions] = useState('');

  // AI panel state
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiNotes, setAiNotes] = useState('');
  const [includePatientData, setIncludePatientData] = useState(true);
  const [includeGrafts, setIncludeGrafts] = useState(true);
  const [analyzeImages, setAnalyzeImages] = useState(true);
  const [currentGraftSummary, setCurrentGraftSummary] = useState(null);
  const [selectedSnippets, setSelectedSnippets] = useState(new Set());
  const [clinicSettings, setClinicSettings] = useState({});

  // Template state
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '', default_attachments: [] });
  const [bulletPoints, setBulletPoints] = useState('');
  const [generatingTemplate, setGeneratingTemplate] = useState(false);

  // Document state
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Lightbox state
  const [lightbox, setLightbox] = useState(null); // { src, label }

  const headers = getAdminHeaders();

  // Reset all reply-related state completely
  const resetReplyState = () => {
    setReplyingTo(null);
    setReplySubject('');
    setReplyBody('');
    setSelectedAttachments([]);
    setAiPanelOpen(false);
    setAiNotes('');
    setIncludePatientData(true);
    setIncludeGrafts(true);
    setAnalyzeImages(true);
    setSelectedSnippets(new Set());
    setRefineOpen(false);
    setRefineInstructions('');
    setCurrentGraftSummary(null);
    setGenerating(false);
    setSending(false);
    setRefining(false);
  };

  const getImageUrl = (inquiryId, imageKey, thumb = true) => {
    const token = localStorage.getItem('adminToken') || '';
    const params = new URLSearchParams({ token });
    if (thumb) params.set('thumb', 'true');
    return `${API}/api/admin/price-inquiries/${inquiryId}/images/${encodeURIComponent(imageKey)}?${params.toString()}`;
  };

  const openLightbox = (inquiryId, imageKey, label) => {
    setLightbox({ src: getImageUrl(inquiryId, imageKey, false), label });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [inqRes, tplRes, docRes, settingsRes] = await Promise.all([
        fetch(`${API}/api/admin/price-inquiries`, { headers }),
        fetch(`${API}/api/admin/reply-templates`, { headers }),
        fetch(`${API}/api/admin/documents`, { headers }),
        fetch(`${API}/api/admin/clinic-settings`, { headers }),
      ]);
      if (inqRes.ok) setInquiries((await inqRes.json()).inquiries || []);
      if (tplRes.ok) setTemplates((await tplRes.json()).templates || []);
      if (docRes.ok) setDocuments((await docRes.json()).documents || []);
      if (settingsRes.ok) setClinicSettings(await settingsRes.json());
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authenticated) fetchData(); }, [authenticated, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      localStorage.setItem('adminToken', data.token);
      setAuthenticated(true);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  // --- Inquiry actions ---
  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/admin/price-inquiries/${id}/status`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
      fetchData();
      toast.success('Status aktualisiert');
    } catch { toast.error('Fehler'); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Anfrage wirklich löschen?')) return;
    try {
      await fetch(`${API}/api/admin/price-inquiries/${id}`, { method: 'DELETE', headers });
      fetchData();
      toast.success('Gelöscht');
    } catch { toast.error('Fehler'); }
  };

  const generateReply = async (id) => {
    setGenerating(true);
    try {
      const body = {
        bullet_points: aiNotes.trim() || null,
        include_patient_data: includePatientData,
        graft_summary: (includeGrafts && currentGraftSummary) ? currentGraftSummary : null,
        selected_snippets: Array.from(selectedSnippets),
        analyze_images: analyzeImages,
      };
      const res = await fetch(`${API}/api/admin/price-inquiries/${id}/generate-reply`, {
        method: 'POST', headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Fehler'); }
      const data = await res.json();
      setReplySubject(data.subject);
      setReplyBody(data.body);
      // Keep panel open + notes intact so user can refine & regenerate
      toast.success('KI-Antwort generiert');
    } catch (err) { toast.error(err.message); }
    finally { setGenerating(false); }
  };

  const sendReply = async (id) => {
    if (!replySubject || !replyBody) { toast.error('Betreff und Text erforderlich'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/price-inquiries/${id}/send-reply`, {
        method: 'POST', headers,
        body: JSON.stringify({ inquiry_id: id, subject: replySubject, body: replyBody, attachment_ids: selectedAttachments }),
      });
      if (!res.ok) throw new Error();
      toast.success('E-Mail gesendet!');
      resetReplyState();
      fetchData();
    } catch { toast.error('Senden fehlgeschlagen'); }
    finally { setSending(false); }
  };

  const refineText = async (inq) => {
    if (!replyBody.trim()) { toast.error('Kein Text zum Verfeinern vorhanden'); return; }
    if (!refineInstructions.trim()) { toast.error('Bitte Anweisungen eingeben'); return; }
    setRefining(true);
    try {
      const res = await fetch(`${API}/api/admin/refine-text`, {
        method: 'POST', headers,
        body: JSON.stringify({
          existing_text: replyBody,
          instructions: refineInstructions,
          patient_name: inq?.name || '',
        }),
      });
      const data = await res.json();
      if (data.success && data.body) {
        setReplyBody(data.body);
        setRefineInstructions('');
        setRefineOpen(false);
        toast.success('Text wurde verfeinert');
      } else {
        toast.error(data.detail || 'Verfeinerung fehlgeschlagen');
      }
    } catch { toast.error('KI-Verfeinerung fehlgeschlagen'); }
    finally { setRefining(false); }
  };

  const startReply = (inq) => {
    // Full reset before starting new reply
    setAiPanelOpen(false);
    setAiNotes('');
    setIncludePatientData(true);
    setIncludeGrafts(true);
    setAnalyzeImages(true);
    setSelectedSnippets(new Set());
    setRefineOpen(false);
    setRefineInstructions('');
    setCurrentGraftSummary(null);
    setGenerating(false);
    setSending(false);
    setRefining(false);
    // Set new reply state
    setReplyingTo(inq.id);
    setReplySubject(inq.reply_subject || '');
    setReplyBody(inq.reply_body || '');
    setSelectedAttachments([]);
  };

  const applyTemplate = (tpl, inq) => {
    setReplySubject(replacePlaceholders(tpl.subject, inq));
    setReplyBody(replacePlaceholders(tpl.body, inq));
    setSelectedAttachments(tpl.default_attachments || []);
    toast.success(`Template "${tpl.name}" angewendet`);
  };

  const toggleAttachment = (docId) => {
    setSelectedAttachments(prev => prev.includes(docId) ? prev.filter(d => d !== docId) : [...prev, docId]);
  };

  // --- Template actions ---
  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Name, Betreff und Text sind erforderlich'); return;
    }
    try {
      if (editingTemplate && editingTemplate !== 'new') {
        await fetch(`${API}/api/admin/reply-templates/${editingTemplate}`, { method: 'PUT', headers, body: JSON.stringify(templateForm) });
      } else {
        await fetch(`${API}/api/admin/reply-templates`, { method: 'POST', headers, body: JSON.stringify(templateForm) });
      }
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body: '', default_attachments: [] });
      setBulletPoints('');
      fetchData();
      toast.success('Template gespeichert');
    } catch { toast.error('Fehler'); }
  };

  const generateTemplateBody = async () => {
    if (!bulletPoints.trim()) { toast.error('Bitte Stichpunkte eingeben'); return; }
    setGeneratingTemplate(true);
    try {
      const res = await fetch(`${API}/api/admin/reply-templates/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ bullet_points: bulletPoints }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Fehler'); }
      const data = await res.json();
      setTemplateForm(f => ({ ...f, body: data.body }));
      toast.success('Text generiert');
    } catch (err) { toast.error(err.message); }
    finally { setGeneratingTemplate(false); }
  };

  const deleteTemplate = async (id) => {    if (!window.confirm('Template wirklich löschen?')) return;
    try {
      await fetch(`${API}/api/admin/reply-templates/${id}`, { method: 'DELETE', headers });
      fetchData();
      toast.success('Gelöscht');
    } catch { toast.error('Fehler'); }
  };

  const startEditTemplate = (tpl) => {
    setEditingTemplate(tpl.id);
    setTemplateForm({ name: tpl.name, subject: tpl.subject, body: tpl.body, default_attachments: tpl.default_attachments || [] });
    setBulletPoints('');
  };

  // --- Document actions ---
  const uploadDocument = async (file) => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/admin/documents`, {
        method: 'POST', headers: { Authorization: getAdminHeaders().Authorization }, body: formData,
      });
      if (!res.ok) throw new Error();
      fetchData();
      toast.success('Dokument hochgeladen');
    } catch { toast.error('Upload fehlgeschlagen'); }
    finally { setUploadingDoc(false); }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;
    try {
      await fetch(`${API}/api/admin/documents/${id}`, { method: 'DELETE', headers });
      fetchData();
      toast.success('Gelöscht');
    } catch { toast.error('Fehler'); }
  };

  // Filtering
  const filtered = inquiries.filter(inq => {
    if (filterStatus !== 'all' && (inq.status || 'new') !== filterStatus) return false;
    if (filterTreatment !== 'all' && inq.treatment !== filterTreatment) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (inq.name || '').toLowerCase().includes(q) || (inq.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const statusCounts = {
    all: inquiries.length,
    new: inquiries.filter(i => (i.status || 'new') === 'new').length,
    replied: inquiries.filter(i => i.status === 'replied').length,
    appointment: inquiries.filter(i => i.status === 'appointment').length,
    completed: inquiries.filter(i => i.status === 'completed').length,
  };

  const jaNein = (v) => v === 'ja' ? 'Ja' : v === 'nein' ? 'Nein' : '-';
  const formatSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

  if (!authenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <form onSubmit={handleLogin} className={`w-full max-w-sm mx-4 rounded-2xl p-8 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
          <h1 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Preisrechner Admin</h1>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-Mail" className={`mb-3 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort" className={`mb-3 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} />
          {loginError && <p className="text-red-400 text-sm mb-3">{loginError}</p>}
          <Button type="submit" className="w-full bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white">Login</Button>
        </form>
      </div>
    );
  }

  // Lightbox Modal
  const LightboxModal = () => lightbox && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={() => setLightbox(null)}
      data-testid="lightbox-overlay"
    >
      <div className="relative max-w-3xl w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-white font-medium text-sm">{lightbox.label}</span>
          <button onClick={() => setLightbox(null)} className="text-white/60 hover:text-white p-1" data-testid="lightbox-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <img
          src={lightbox.src}
          alt={lightbox.label}
          className="w-full rounded-2xl object-contain max-h-[80vh] shadow-2xl"
          style={{ background: '#111' }}
          data-testid="lightbox-image"
        />
        <p className="text-white/30 text-xs text-center mt-2">Klicken zum Schließen</p>
      </div>
    </div>
  );

  const tabs = [
    { id: 'inquiries', icon: <Calculator className="w-4 h-4" />, label: t(language, 'Anfragen', 'Inquiries') },
    { id: 'templates', icon: <FileText className="w-4 h-4" />, label: t(language, 'Templates', 'Templates') },
    { id: 'documents', icon: <Paperclip className="w-4 h-4" />, label: t(language, 'Dokumente', 'Documents') },
    { id: 'settings',  icon: <Settings className="w-4 h-4" />,  label: t(language, 'Einstellungen', 'Settings') },
  ];

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`} style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
      <LightboxModal />
      <header className={`sticky top-0 z-40 backdrop-blur-xl border-b ${isDark ? 'bg-[#0a0a0a]/90 border-white/5' : 'bg-white/90 border-gray-200'}`} style={{ touchAction: 'manipulation' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t(language, 'Preisrechner-Anfragen', 'Price Calculator Inquiries')}
          </h1>
          <div className="flex items-center gap-1">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${activeTab === tab.id ? (isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]') : (isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-700')}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* ===== INQUIRIES TAB ===== */}
        {activeTab === 'inquiries' && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
              {[
                { key: 'all', label: 'Gesamt', icon: <Calculator className="w-4 h-4" /> },
                { key: 'new', label: 'Neu', icon: <Mail className="w-4 h-4" /> },
                { key: 'replied', label: 'Beantwortet', icon: <Send className="w-4 h-4" /> },
                { key: 'appointment', label: 'Termin', icon: <Clock className="w-4 h-4" /> },
                { key: 'completed', label: 'Abgeschlossen', icon: <CheckCircle className="w-4 h-4" /> },
              ].map(s => (
                <button key={s.key} onClick={() => setFilterStatus(s.key)}
                  className={`rounded-xl p-3 border text-left transition-all ${filterStatus === s.key ? (isDark ? 'border-[#7BA7C2]/50 bg-[#7BA7C2]/10' : 'border-[#5A8BA6]/50 bg-[#5A8BA6]/5') : (isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm')}`}
                  data-testid={`filter-${s.key}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={isDark ? 'text-white/40' : 'text-gray-400'}>{s.icon}</span>
                    <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{statusCounts[s.key]}</span>
                  </div>
                  <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{s.label}</div>
                </button>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-white/30' : 'text-gray-400'}`} />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Name oder E-Mail suchen..."
                  className={`pl-10 ${isDark ? 'bg-white/5 border-white/10 text-white' : ''}`} data-testid="inquiry-search" />
              </div>
              <select value={filterTreatment} onChange={e => setFilterTreatment(e.target.value)}
                className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-700'}`} data-testid="filter-treatment">
                <option value="all">Alle Behandlungen</option>
                <option value="haar">Haartransplantation</option>
                <option value="bart">Barttransplantation</option>
                <option value="augenbrauen">Augenbrauentransplantation</option>
              </select>
            </div>

            {loading && <div className="text-center py-12"><Loader2 className={`animate-spin w-8 h-8 mx-auto ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} /></div>}

            {!loading && <div className="space-y-3">
              {filtered.length === 0 && (
                <div className={`text-center py-12 rounded-2xl border ${isDark ? 'bg-[#111] border-white/5 text-white/40' : 'bg-white border-gray-100 text-gray-400'}`}>
                  Keine Anfragen gefunden.
                </div>
              )}

              {filtered.map(inq => {
                const status = statusConfig[inq.status || 'new'] || statusConfig.new;
                const isExpanded = expandedId === inq.id;
                const isReplying = replyingTo === inq.id;

                return (
                  <div key={inq.id} className={`rounded-2xl border overflow-clip ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                    {/* Row header */}
                    <div className="px-5 py-4 flex items-center justify-between cursor-pointer"
                      onClick={() => {
                        if (isExpanded) {
                          setExpandedId(null);
                          resetReplyState();
                        } else {
                          // Switching to different inquiry: reset old reply state first
                          if (replyingTo && replyingTo !== inq.id) resetReplyState();
                          setExpandedId(inq.id);
                        }
                      }}
                      data-testid={`inquiry-row-${inq.id}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${status.color}`}>{status.de}</span>
                        <div className="min-w-0">
                          <div className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{inq.name}</div>
                          <div className={`text-xs truncate ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{inq.email} &middot; {treatmentLabels[inq.treatment] || inq.treatment}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`text-xs hidden sm:block ${isDark ? 'text-white/30' : 'text-gray-300'}`}>{new Date(inq.created_at).toLocaleDateString('de-DE')}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className={`px-5 pb-5 pt-2 border-t ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mb-4">
                          {[
                            { label: 'Behandlung', value: treatmentLabels[inq.treatment] || inq.treatment },
                            { label: 'Muster/Bereich', value: inq.pattern || '-' },
                            { label: 'Geschlecht', value: inq.gender === 'mann' ? 'Mann' : inq.gender === 'frau' ? 'Frau' : '-' },
                            { label: 'Alter', value: inq.age || '-' },
                            { label: 'Haarausfall seit', value: inq.hair_loss_since || '-' },
                            { label: 'Zeitrahmen', value: timelineLabels[inq.timeline] || inq.timeline || '-' },
                            { label: 'Vorerkrankungen', value: inq.pre_existing === 'ja' ? `Ja (${inq.pre_existing_details || ''})` : jaNein(inq.pre_existing) },
                            { label: 'Frühere Transplantation', value: jaNein(inq.previous_transplant) },
                            { label: 'Medikamente', value: inq.current_medications || '-' },
                            { label: 'Methode', value: ({ classic: 'Klassisch (FUE Saphir)', dhi: 'Ohne Rasur Oberkopf (Saphir-DHI Hybrid)', longhair: 'Long Hair FUE (komplett ohne Rasur)' })[inq.method] || inq.method || (inq.no_shave === 'ja' ? 'DHI (ohne Rasur)' : '-') },
                            ...(inq.treatment === 'haar' ? [
                              { label: 'Schmerzfreie Betäubung', value: jaNein(inq.pain_free_anesthesia) },
                              { label: 'PRP-Behandlung', value: jaNein(inq.prp_treatment) },
                            ] : []),
                            { label: 'Aufmerksam geworden über', value: ({ google: 'Google', instagram: 'Instagram', tiktok: 'TikTok', empfehlung: 'Empfehlung', werbetafel: 'Werbetafel', sonstiges: 'Sonstiges' })[inq.referral_source] || inq.referral_source || '-' },
                            { label: 'Telefon', value: inq.phone || '-' },
                            { label: 'Datum', value: new Date(inq.created_at).toLocaleString('de-DE') },
                          ].map((item, i) => (
                            <div key={i} className="flex justify-between py-1.5 border-b border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
                              <span className={`text-xs font-medium ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{item.label}</span>
                              <span className={`text-xs text-right ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{item.value}</span>
                            </div>
                          ))}
                        </div>

                        {/* Patient Images */}
                        {inq.has_images && (
                          <div className={`mb-4 p-4 rounded-xl border ${isDark ? 'bg-white/3 border-white/5' : 'bg-gray-50 border-gray-100'}`} data-testid={`images-section-${inq.id}`}>
                            <div className={`flex items-center gap-2 mb-3 text-xs font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                              <Image className="w-3.5 h-3.5" />
                              Hochgeladene Bilder
                            </div>
                            {(inq.image_keys || []).length > 0 ? (
                              <div className="flex flex-wrap gap-3">
                                {(inq.image_keys || []).map(imgKey => (
                                  <button
                                    key={imgKey}
                                    onClick={() => openLightbox(inq.id, imgKey, imgKey)}
                                    className="group relative rounded-xl overflow-hidden border-2 transition-all hover:scale-105"
                                    style={{ borderColor: isDark ? 'rgba(123,167,194,0.3)' : 'rgba(90,139,166,0.3)' }}
                                    data-testid={`image-thumb-${inq.id}-${imgKey}`}
                                    title={imgKey}
                                  >
                                    <img
                                      src={getImageUrl(inq.id, imgKey, true)}
                                      alt={imgKey}
                                      className="w-24 h-24 object-cover"
                                      onError={e => { e.target.parentElement.style.display = 'none'; }}
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                      <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-0.5">
                                      <span className="text-white/80 text-[10px] truncate block">{imgKey}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Bilder wurden per E-Mail gesendet (ältere Anfrage)</p>
                            )}
                          </div>
                        )}

                        {/* Reply History */}
                        {(inq.reply_history?.length > 0 || inq.replied_at) && (
                          <div className="mb-4 space-y-2">
                            {(inq.reply_history || [{ subject: inq.reply_subject, body: inq.reply_body, sent_at: inq.replied_at }]).map((reply, idx) => (
                              <details key={idx} className={`rounded-xl border overflow-clip ${isDark ? 'bg-green-500/5 border-green-500/10' : 'bg-green-50 border-green-100'}`}>
                                <summary className={`px-4 py-2.5 cursor-pointer text-xs flex items-center gap-2 ${isDark ? 'text-green-400 hover:bg-green-500/5' : 'text-green-700 hover:bg-green-100/50'}`} style={{ touchAction: 'manipulation' }}>
                                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="font-medium">
                                    {reply.sent_at ? new Date(reply.sent_at).toLocaleString('de-DE') : 'Gesendet'}
                                  </span>
                                  <span className={`truncate ${isDark ? 'text-white/40' : 'text-gray-500'}`}>– {reply.subject || 'Kein Betreff'}</span>
                                </summary>
                                <div className={`px-4 pb-3 pt-1 text-xs border-t ${isDark ? 'border-green-500/10' : 'border-green-100'}`}>
                                  <div className={`font-medium mb-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Betreff: {reply.subject}</div>
                                  <div className={`whitespace-pre-wrap leading-relaxed ${isDark ? 'text-white/45' : 'text-gray-500'}`}>
                                    {reply.body || 'Kein Text gespeichert'}
                                  </div>
                                  {reply.attachment_count > 0 && (
                                    <div className={`mt-2 text-[10px] ${isDark ? 'text-white/25' : 'text-gray-400'}`}>
                                      {reply.attachment_count} Anhang/Anhänge
                                    </div>
                                  )}
                                </div>
                              </details>
                            ))}
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex gap-2 flex-wrap mb-4">
                          <Button variant="outline" onClick={() => isReplying ? resetReplyState() : startReply(inq)}
                            className={`text-sm min-h-[42px] ${isDark ? 'text-white border-white/20' : ''}`} data-testid={`reply-btn-${inq.id}`}>
                            <Mail className="w-4 h-4 mr-1" />
                            {isReplying ? 'Abbrechen' : 'Antworten'}
                          </Button>
                          {status.next && (
                            <Button variant="outline" onClick={() => updateStatus(inq.id, status.next)}
                              className={`text-sm min-h-[42px] ${isDark ? 'text-white border-white/20' : ''}`} data-testid={`status-btn-${inq.id}`}>
                              <CheckCircle className="w-4 h-4 mr-1" /> &rarr; {statusConfig[status.next]?.de}
                            </Button>
                          )}
                          <Button variant="outline" onClick={() => deleteInquiry(inq.id)}
                            className="text-sm min-h-[42px] text-red-400 border-red-400/30 hover:bg-red-400/10">
                            <Trash2 className="w-4 h-4 mr-1" /> Löschen
                          </Button>
                        </div>

                        {/* Reply form */}
                        {isReplying && (
                          <div className={`p-4 rounded-xl relative z-10 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                              <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Antwort an {inq.name} ({inq.email})
                              </h4>
                              <div className="flex gap-2 items-center">
                                {/* Template selector */}
                                {templates.length > 0 && (
                                  <select onChange={e => { const tpl = templates.find(t => t.id === e.target.value); if (tpl) applyTemplate(tpl, inq); e.target.value = ''; }}
                                    className={`px-2 py-2 rounded-lg border text-xs min-h-[40px] ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-700'}`}
                                    data-testid={`template-select-${inq.id}`}>
                                    <option value="">Template wählen...</option>
                                    {templates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
                                  </select>
                                )}
                                <Button variant="outline" onClick={() => setAiPanelOpen(o => !o)}
                                  className={`text-xs min-h-[40px] ${isDark ? 'text-[#7BA7C2] border-[#7BA7C2]/30' : 'text-[#5A8BA6] border-[#5A8BA6]/30'}`}
                                  data-testid={`generate-reply-${inq.id}`}>
                                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                                  KI-Antwort
                                </Button>
                              </div>
                            </div>

                            {/* AI Panel */}
                            {aiPanelOpen && (
                              <div className={`rounded-xl border p-4 mb-2 ${isDark ? 'bg-[#7BA7C2]/5 border-[#7BA7C2]/15' : 'bg-[#5A8BA6]/4 border-[#5A8BA6]/15'}`} data-testid="ai-panel">
                                <div className={`flex items-center gap-1.5 mb-3 text-xs font-semibold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                                  <Sparkles className="w-3.5 h-3.5" />
                                  KI-Assistent
                                </div>

                                {/* Bullet points */}
                                <div className="mb-3">
                                  <label className={`text-xs flex items-center gap-1.5 mb-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                    Stichpunkte / Hinweise <span className={`${isDark ? 'text-white/20' : 'text-gray-300'}`}>(optional)</span>
                                    <VoiceInput onResult={t => setAiNotes(prev => prev ? prev + ' ' + t : t)} className="w-6 h-6 ml-auto" />
                                  </label>
                                  <textarea
                                    value={aiNotes}
                                    onChange={e => setAiNotes(e.target.value)}
                                    rows={3}
                                    placeholder={"- Beratungstermin anbieten\n- PRP-Behandlung erwähnen\n- Kurze und freundliche Antwort"}
                                    className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                                    data-testid="ai-notes-input"
                                  />
                                </div>

                                {/* Checkboxes */}
                                <div className="flex flex-wrap gap-4 mb-4">
                                  <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                      type="checkbox"
                                      checked={includePatientData}
                                      onChange={e => setIncludePatientData(e.target.checked)}
                                      className="w-3.5 h-3.5 rounded accent-[#7BA7C2]"
                                      data-testid="include-patient-data-cb"
                                    />
                                    <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Patientendaten einbeziehen</span>
                                  </label>
                                  {inq.image_keys?.length > 0 && (
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={analyzeImages}
                                        onChange={e => setAnalyzeImages(e.target.checked)}
                                        className="w-3.5 h-3.5 rounded accent-[#7BA7C2]"
                                        data-testid="analyze-images-cb"
                                      />
                                      <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Bilder analysieren ({inq.image_keys.length})</span>
                                    </label>
                                  )}
                                  {currentGraftSummary && (
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={includeGrafts}
                                        onChange={e => setIncludeGrafts(e.target.checked)}
                                        className="w-3.5 h-3.5 rounded accent-[#7BA7C2]"
                                        data-testid="include-grafts-cb"
                                      />
                                      <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Graftanzahl einbeziehen</span>
                                    </label>
                                  )}
                                </div>

                                {/* Contact info snippets */}
                                {(() => {
                                  const SNIPPETS = [
                                    { key: 'cal_phone',  label: 'Buchungslink Telefon',  value: clinicSettings.cal_phone },
                                    { key: 'cal_onsite', label: 'Buchungslink Vor-Ort',  value: clinicSettings.cal_onsite },
                                    { key: 'phone',      label: 'Festnetz',              value: clinicSettings.phone },
                                    { key: 'whatsapp',   label: 'WhatsApp',              value: clinicSettings.whatsapp },
                                    { key: 'address',    label: 'Adresse',               value: clinicSettings.address },
                                    { key: 'email',      label: 'E-Mail',                value: clinicSettings.email },
                                  ].filter(s => s.value);
                                  if (!SNIPPETS.length) return (
                                    <p className={`text-xs italic mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`}>
                                      Klinik-Einstellungen hinterlegen um Kontaktinfos einzubauen
                                    </p>
                                  );
                                  return (
                                    <div className="mb-3">
                                      <p className={`text-xs mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Kontaktinfos einbauen</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {SNIPPETS.map(s => {
                                          const on = selectedSnippets.has(s.key);
                                          return (
                                            <button
                                              key={s.key}
                                              onClick={() => setSelectedSnippets(prev => {
                                                const n = new Set(prev); on ? n.delete(s.key) : n.add(s.key); return n;
                                              })}
                                              className={`px-3 py-2 rounded-full text-xs border transition-all min-h-[36px] ${
                                                on
                                                  ? (isDark ? 'bg-[#7BA7C2]/20 border-[#7BA7C2]/50 text-[#7BA7C2]' : 'bg-[#5A8BA6]/15 border-[#5A8BA6]/40 text-[#5A8BA6]')
                                                  : (isDark ? 'bg-white/3 border-white/10 text-white/40 hover:border-white/20' : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300')
                                              }`}
                                              data-testid={`snippet-chip-${s.key}`}
                                            >
                                              {on ? '✓ ' : '+ '}{s.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })()}

                                <Button
                                  onClick={() => generateReply(inq.id)}
                                  disabled={generating}
                                  className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-xs min-h-[40px] relative z-10"
                                  data-testid={`ai-generate-btn-${inq.id}`}
                                >
                                  {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                                  {generating ? 'Generiert...' : 'Text generieren'}
                                </Button>
                              </div>
                            )}

                            <div className="space-y-3">
                              <div>
                                <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Betreff</label>
                                <Input value={replySubject} onChange={e => setReplySubject(e.target.value)} placeholder="E-Mail Betreff..."
                                  className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid={`reply-subject-${inq.id}`} />
                              </div>
                              <div>
                                <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Nachricht</label>
                                <textarea value={replyBody} onChange={e => setReplyBody(e.target.value)} rows={10}
                                  placeholder="Antworttext eingeben, Template wählen oder per KI generieren..."
                                  className={`w-full px-3 py-2 rounded-lg border text-sm resize-y ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                                  data-testid={`reply-body-${inq.id}`} />

                                {/* KI-Verfeinern */}
                                {replyBody.trim() && (
                                  <div className="mt-2">
                                    {!refineOpen ? (
                                      <button
                                        onClick={() => setRefineOpen(true)}
                                        className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${isDark ? 'text-[#7BA7C2]/70 hover:text-[#7BA7C2]' : 'text-[#5A8BA6]/70 hover:text-[#5A8BA6]'}`}
                                        data-testid={`refine-toggle-${inq.id}`}
                                      >
                                        <Sparkles className="w-3 h-3" />
                                        Text per KI verfeinern
                                      </button>
                                    ) : (
                                      <div className={`rounded-lg border p-3 ${isDark ? 'bg-[#7BA7C2]/5 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/5 border-[#5A8BA6]/15'}`}>
                                        <label className={`text-xs font-medium flex items-center gap-1.5 mb-1.5 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                                          <Sparkles className="w-3 h-3" />
                                          Anweisungen zur Verfeinerung
                                          <VoiceInput onResult={t => setRefineInstructions(prev => prev ? prev + ' ' + t : t)} className="w-6 h-6 ml-auto" />
                                        </label>
                                        <textarea
                                          value={refineInstructions}
                                          onChange={e => setRefineInstructions(e.target.value)}
                                          rows={2}
                                          placeholder="z.B. 'ergänze dass wir natürliche Haarlinien gestalten', 'füge Preis 3.500€ ein', 'mach den Ton persönlicher'"
                                          className={`w-full px-3 py-2 rounded-lg border text-sm mb-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                                          data-testid={`refine-input-${inq.id}`}
                                        />
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            onClick={() => refineText(inq)}
                                            disabled={refining || !refineInstructions.trim()}
                                            className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-xs min-h-[36px]"
                                            data-testid={`refine-btn-${inq.id}`}
                                          >
                                            {refining ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                                            {refining ? 'Verfeinere...' : 'Verfeinern'}
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => { setRefineOpen(false); setRefineInstructions(''); }}
                                            className={`text-xs min-h-[36px] ${isDark ? 'text-white/60 border-white/10' : ''}`}
                                          >
                                            Abbrechen
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Graft Calculator */}
                              <GraftCalculator
                                treatment={inq.treatment}
                                onInsert={(text) => setReplyBody(prev => prev + text)}
                                onGraftChange={(summary) => setCurrentGraftSummary(summary)}
                              />

                              {/* Attachments */}
                              {documents.length > 0 && (
                                <div>
                                  <label className={`text-xs block mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                    <Paperclip className="w-3 h-3 inline mr-1" />Anhänge
                                  </label>
                                  <div className="flex flex-wrap gap-2">
                                    {documents.map(doc => (
                                      <button key={doc.id} onClick={() => toggleAttachment(doc.id)}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all border ${
                                          selectedAttachments.includes(doc.id)
                                            ? (isDark ? 'bg-[#7BA7C2]/20 border-[#7BA7C2]/50 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/50 text-[#5A8BA6]')
                                            : (isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-white border-gray-200 text-gray-500')
                                        }`}
                                        data-testid={`attachment-${doc.id}`}>
                                        <File className="w-3 h-3" />
                                        {doc.name}
                                        {selectedAttachments.includes(doc.id) && <CheckCircle className="w-3 h-3" />}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="flex gap-2 pt-1">
                                <Button onClick={() => sendReply(inq.id)} disabled={sending || !replySubject || !replyBody}
                                  className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm min-h-[44px]" data-testid={`send-reply-${inq.id}`}>
                                  {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                                  {sending ? 'Wird gesendet...' : `E-Mail senden${selectedAttachments.length ? ` (${selectedAttachments.length} Anhang${selectedAttachments.length > 1 ? 'e' : ''})` : ''}`}
                                </Button>
                                <Button variant="outline" onClick={() => resetReplyState()} className={`text-sm min-h-[44px] ${isDark ? 'text-white border-white/20' : ''}`}>Abbrechen</Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>}
          </>
        )}

        {/* ===== TEMPLATES TAB ===== */}
        {activeTab === 'templates' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Antwort-Templates</h2>
                <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Vorlagen erstellen und bei Anfragen verwenden</p>
              </div>
              {!editingTemplate && (
                <Button onClick={() => { setEditingTemplate('new'); setTemplateForm({ name: '', subject: '', body: '', default_attachments: [] }); setBulletPoints(''); }}
                  className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm" data-testid="create-template-btn">
                  <Plus className="w-4 h-4 mr-1" /> Neues Template
                </Button>
              )}
            </div>

            {/* Template editor */}
            {editingTemplate && (
              <div className={`rounded-2xl p-5 border mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {editingTemplate === 'new' ? 'Neues Template' : 'Template bearbeiten'}
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Template-Name</label>
                    <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="z.B. Standard-Antwort Haartransplantation"
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="template-name" />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Betreff</label>
                    <Input value={templateForm.subject} onChange={e => setTemplateForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder="z.B. Ihre Anfrage zur {behandlung} - Dion Hair Clinic"
                      className={isDark ? 'bg-white/5 border-white/10 text-white' : ''} data-testid="template-subject" />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Nachrichtentext</label>

                    {/* AI text generation from bullet points */}
                    <div className={`mb-2 p-3 rounded-xl border ${isDark ? 'bg-[#7BA7C2]/5 border-[#7BA7C2]/15' : 'bg-[#5A8BA6]/5 border-[#5A8BA6]/15'}`}>
                      <div className={`flex items-center gap-1.5 mb-2 text-xs font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                        <Sparkles className="w-3.5 h-3.5" />
                        KI-Textgenerierung aus Stichpunkten
                        <VoiceInput onResult={t => setBulletPoints(prev => prev ? prev + ' ' + t : t)} className="w-6 h-6 ml-auto" />
                      </div>
                      <textarea
                        value={bulletPoints}
                        onChange={e => setBulletPoints(e.target.value)}
                        rows={3}
                        placeholder={"- Haartransplantation, Beratungstermin anbieten\n- PRP-Behandlung erwähnen\n- Freundlich, unter 150 Wörter"}
                        className={`w-full px-3 py-2 rounded-lg border text-sm resize-none mb-2 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                        data-testid="bullet-points-input"
                      />
                      <Button
                        onClick={generateTemplateBody}
                        disabled={generatingTemplate || !bulletPoints.trim()}
                        className={`text-xs h-8 ${isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2] hover:bg-[#7BA7C2]/30 border border-[#7BA7C2]/30' : 'bg-[#5A8BA6]/10 text-[#5A8BA6] hover:bg-[#5A8BA6]/20 border border-[#5A8BA6]/20'}`}
                        variant="outline"
                        data-testid="generate-template-body-btn"
                      >
                        {generatingTemplate ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        {generatingTemplate ? 'Generiert...' : 'Text generieren'}
                      </Button>
                    </div>

                    <textarea value={templateForm.body} onChange={e => setTemplateForm(f => ({ ...f, body: e.target.value }))} rows={10}
                      placeholder="Sehr geehrte/r {name},&#10;&#10;vielen Dank für Ihre Anfrage..."
                      className={`w-full px-3 py-2 rounded-lg border text-sm resize-y ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                      data-testid="template-body" />
                  </div>

                  {/* Placeholder hints */}
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-blue-50'}`}>
                    <div className={`text-xs font-medium mb-2 ${isDark ? 'text-white/60' : 'text-blue-700'}`}>Verfügbare Platzhalter:</div>
                    <div className="flex flex-wrap gap-2">
                      {PLACEHOLDERS.map(p => (
                        <span key={p.key} className={`text-xs px-2 py-1 rounded font-mono ${isDark ? 'bg-white/10 text-white/70' : 'bg-blue-100 text-blue-700'}`}>
                          {p.key} <span className="font-sans opacity-60">= {p.desc}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Default attachments */}
                  {documents.length > 0 && (
                    <div>
                      <label className={`text-xs block mb-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        <Paperclip className="w-3 h-3 inline mr-1" />Standard-Anhänge (werden automatisch beigefügt)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {documents.map(doc => {
                          const isSelected = templateForm.default_attachments.includes(doc.id);
                          return (
                            <button key={doc.id} onClick={() => setTemplateForm(f => ({
                              ...f,
                              default_attachments: isSelected
                                ? f.default_attachments.filter(d => d !== doc.id)
                                : [...f.default_attachments, doc.id]
                            }))}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all border ${
                                isSelected
                                  ? (isDark ? 'bg-[#7BA7C2]/20 border-[#7BA7C2]/50 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/50 text-[#5A8BA6]')
                                  : (isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-white border-gray-200 text-gray-500')
                              }`}
                              data-testid={`tpl-attach-${doc.id}`}>
                              <File className="w-3 h-3" />{doc.name}
                              {isSelected && <CheckCircle className="w-3 h-3" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={saveTemplate} className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white text-sm" data-testid="save-template-btn">
                      <Save className="w-4 h-4 mr-1" /> Speichern
                    </Button>
                    <Button variant="outline" onClick={() => setEditingTemplate(null)} className={`text-sm ${isDark ? 'text-white border-white/20' : ''}`}>Abbrechen</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Template list */}
            <div className="space-y-3">
              {templates.length === 0 && !editingTemplate && (
                <div className={`text-center py-12 rounded-2xl border ${isDark ? 'bg-[#111] border-white/5 text-white/40' : 'bg-white border-gray-100 text-gray-400'}`}>
                  Noch keine Templates erstellt. Erstelle dein erstes Template!
                </div>
              )}
              {templates.map(tpl => (
                <div key={tpl.id} className={`rounded-2xl border p-5 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tpl.name}</h3>
                      <div className={`text-xs mt-0.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Betreff: {tpl.subject}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => startEditTemplate(tpl)} className={`text-xs ${isDark ? 'text-white border-white/20' : ''}`} data-testid={`edit-tpl-${tpl.id}`}>
                        Bearbeiten
                      </Button>
                      <Button variant="outline" onClick={() => deleteTemplate(tpl.id)} className="text-xs text-red-400 border-red-400/30 hover:bg-red-400/10">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className={`text-sm whitespace-pre-line line-clamp-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{tpl.body}</div>
                  {tpl.default_attachments && tpl.default_attachments.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <Paperclip className={`w-3 h-3 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
                      <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {tpl.default_attachments.length} Anhang{tpl.default_attachments.length > 1 ? 'e' : ''}:
                        {tpl.default_attachments.map(aid => documents.find(d => d.id === aid)?.name || '?').join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ===== DOCUMENTS TAB ===== */}
        {activeTab === 'documents' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Dokumente & Anhänge</h2>
                <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>PDFs und Broschüren für E-Mail-Anhänge verwalten</p>
              </div>
              <label className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white`}>
                <Upload className="w-4 h-4" />
                {uploadingDoc ? 'Lädt...' : 'Dokument hochladen'}
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={e => uploadDocument(e.target.files?.[0])} className="hidden" data-testid="doc-upload-input" />
              </label>
            </div>

            <div className="space-y-3">
              {documents.length === 0 && (
                <div className={`text-center py-12 rounded-2xl border ${isDark ? 'bg-[#111] border-white/5 text-white/40' : 'bg-white border-gray-100 text-gray-400'}`}>
                  Noch keine Dokumente hochgeladen.
                </div>
              )}
              {documents.map(doc => (
                <div key={doc.id} className={`rounded-2xl border p-4 flex items-center justify-between ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
                      <File className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{doc.name}</div>
                      <div className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {formatSize(doc.size)} &middot; {new Date(doc.created_at).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <a href={doc.url} target="_blank" rel="noopener noreferrer"
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'}`}>
                      <Eye className="w-4 h-4" />
                    </a>
                    <button onClick={() => deleteDocument(doc.id)}
                      className="p-2 rounded-lg transition-colors text-red-400 hover:bg-red-400/10" data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl">
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Klinik-Einstellungen</h2>
              <p className={`text-sm mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                Diese Infos stehen im KI-Antwort-Assistent als anklickbare Chips zur Verfügung.
              </p>
            </div>

            {[
              { key: 'cal_phone',  label: 'Cal.com – Telefonberatung',   placeholder: 'https://cal.com/...',       hint: 'Buchungslink für Telefonberatungen' },
              { key: 'cal_onsite', label: 'Cal.com – Vor-Ort-Termin',    placeholder: 'https://cal.com/...',       hint: 'Buchungslink für persönliche Beratungen' },
              { key: 'phone',      label: 'Festnetz-Telefonnummer',       placeholder: '+49 2161 ...',              hint: 'Klinik-Telefonnummer' },
              { key: 'whatsapp',   label: 'WhatsApp',                     placeholder: '+49 176 ... oder wa.me/...',hint: 'WhatsApp-Nummer oder direkter Link' },
              { key: 'address',    label: 'Adresse',                      placeholder: 'Straße, PLZ Ort',          hint: 'Klinik-Adresse' },
              { key: 'email',      label: 'Klinik-E-Mail',                placeholder: 'info@dionhairclinic.de',   hint: 'Kontakt-E-Mail der Klinik' },
            ].map(field => (
              <div key={field.key} className={`rounded-xl border p-4 mb-3 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                <label className={`text-sm font-medium block mb-0.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{field.label}</label>
                <p className={`text-xs mb-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{field.hint}</p>
                <input
                  type="text"
                  value={clinicSettings[field.key] || ''}
                  onChange={e => setClinicSettings(s => ({ ...s, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                  data-testid={`clinic-setting-${field.key}`}
                />
              </div>
            ))}

            {/* AI Model Selection */}
            <div className="mt-6 mb-4">
              <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>KI-Modell</h3>
              <p className={`text-xs ${isDark ? 'text-white/35' : 'text-gray-400'}`}>
                Modell für die KI-Antwortgenerierung bei Preisrechner-Anfragen (inkl. Bildanalyse).
              </p>
            </div>
            <div className={`rounded-xl border p-4 mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <select
                value={clinicSettings.ai_model || 'claude-sonnet-4-5'}
                onChange={e => setClinicSettings(s => ({ ...s, ai_model: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                data-testid="ai-model-select"
              >
                <option value="claude-sonnet-4-5">Claude Sonnet 4.5 (Anthropic) – Standard</option>
                <option value="claude-sonnet-5">Claude Sonnet 5 (Anthropic)</option>
                <option value="claude-opus-4-8">Claude Opus 4.8 (Anthropic)</option>
                <option value="claude-opus-5">Claude Opus 5 (Anthropic)</option>
                <option value="gpt-5.6-sol">GPT-5.6 Sol (OpenAI)</option>
                <option value="gpt-6-astra">GPT-6 Astra (OpenAI) – Neu</option>
              </select>
              {(clinicSettings.ai_model === 'gpt-6-astra') && (
                <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`} data-testid="astra-hint">
                  GPT-6 Astra läuft über Ihren eigenen OpenAI API-Key (bereits hinterlegt), da das Modell im Universal Key noch nicht freigeschaltet ist. Sobald verfügbar, wird automatisch der Universal Key genutzt.
                </p>
              )}
            </div>

            {/* Notification Email Settings */}
            <div className="mt-6 mb-4">
              <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>KI-Analyse Anweisungen</h3>
              <p className={`text-xs ${isDark ? 'text-white/35' : 'text-gray-400'}`}>
                Diese Anweisungen werden bei jeder KI-Antwortgenerierung automatisch mitgesendet. Definieren Sie hier den Antwortstil, Rahmenbedingungen und zusätzliche Informationen.
              </p>
            </div>
            <div className={`rounded-xl border p-4 mb-6 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
              <label className={`text-sm font-medium block mb-0.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>Standard-Anweisungen</label>
              <p className={`text-xs mb-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                Z.B. Antwortstil, Preisbereiche, Methoden-Empfehlungen, Einschränkungen der Bildanalyse
              </p>
              <textarea
                value={clinicSettings.ai_instructions || ''}
                onChange={e => setClinicSettings(s => ({ ...s, ai_instructions: e.target.value }))}
                rows={6}
                placeholder={"- Antwortsprache: Professionell aber herzlich\n- Nenne keine konkreten Preise ohne Rücksprache\n- Bei der Bildanalyse: Geschätzten Norwood-Grad erwähnen\n- Empfehle immer ein kostenloses Beratungsgespräch\n- Erwähne die FUE Saphir-Technologie"}
                className={`w-full px-3 py-2 rounded-lg border text-sm resize-y ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                data-testid="ai-instructions-textarea"
              />
            </div>

            <div className="mt-6 mb-4">
              <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>E-Mail-Benachrichtigungen</h3>
              <p className={`text-xs ${isDark ? 'text-white/35' : 'text-gray-400'}`}>
                Zusätzliche E-Mail-Adressen die bei neuen Anfragen benachrichtigt werden (Komma-getrennt). Gesendete Antworten werden als Kopie (BCC) an diese Adressen geschickt.
              </p>
            </div>
            {[
              { key: 'notification_emails_inquiry', label: 'Preisanfragen – Benachrichtigungen', placeholder: 'kollege1@klinik.de, kollege2@klinik.de', hint: 'Wird bei neuen Preisanfragen + als BCC bei Antworten benachrichtigt' },
              { key: 'notification_emails_contact', label: 'Kontaktanfragen – Benachrichtigungen', placeholder: 'kollege1@klinik.de, kollege2@klinik.de', hint: 'Wird bei neuen Kontaktanfragen benachrichtigt' },
            ].map(field => (
              <div key={field.key} className={`rounded-xl border p-4 mb-3 ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
                <label className={`text-sm font-medium block mb-0.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{field.label}</label>
                <p className={`text-xs mb-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{field.hint}</p>
                <input
                  type="text"
                  value={clinicSettings[field.key] || ''}
                  onChange={e => setClinicSettings(s => ({ ...s, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/20' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-300'}`}
                  data-testid={`clinic-setting-${field.key}`}
                />
              </div>
            ))}

            <Button
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/api/admin/clinic-settings`, { method: 'PUT', headers, body: JSON.stringify(clinicSettings) });
                  if (!res.ok) throw new Error();
                  toast.success('Einstellungen gespeichert');
                } catch { toast.error('Fehler beim Speichern'); }
              }}
              className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white mt-2"
              data-testid="save-clinic-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" /> Einstellungen speichern
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceInquiryAdmin;
